import {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
  useMemo,
  useContext,
} from 'react';

import { gql, useApolloClient } from '@apollo/client';
import {
  Input,
  AlienIcon,
  SearchIcon,
  Dropdown,
  IDropdownItem,
  Button,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { useClients } from 'hooks';
import { LocalizationContext } from 'localization';
import type { TClient } from 'types/user';
import { getRandomNumber } from 'utils';

import s from './ClientSuggest.module.scss';
import { IClientSuggestProps } from './interfaces';

export const ClientSuggest: FunctionComponent<IClientSuggestProps> = ({
  className = '',
  style,
  onChange,
  value,
  small,
  filterClientsFn,
  showBorder,
  disabled,
  placeholder,
  worker = false,
  onFocus,
  reviews,
  isButtonClear = false,
  withReplenishment,
  setIsDefaultClient,
  setDepositOpen,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const { inputValue, setInputValue, clients } = useClients(
    filterClientsFn,
    worker
  );
  const apolloClient = useApolloClient();
  const rootRef = useRef<HTMLDivElement>(null);
  const [visible, setVisibility] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (!rootRef.current) return;

      if (!rootRef.current.contains(e.target as Node)) {
        setVisibility(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  useEffect(() => {
    if (!value) {
      return setInputValue('');
    }

    const selectedClient = apolloClient.readFragment<Pick<TClient, 'phone'>>({
      id: apolloClient.cache.identify({
        __typename: 'User',
        uuid: value,
      }),
      fragment: gql`
        fragment ClientPhone on User {
          phone
        }
      `,
    });
    setInputValue(selectedClient?.phone || '');
  }, [value, setInputValue]);

  useEffect(() => {
    if (!onFocus || !inputRef.current) return;

    inputRef.current?.focus();
  }, [onFocus, inputRef.current]);

  const shouldShowSuggests = visible && clients?.length > 0 && !!inputValue;
  const items = useMemo<Array<IDropdownItem<string, TClient>>>(
    () =>
      clients.map((client) => ({
        value: client.uuid,
        original: client,
      })),
    [clients]
  );

  return (
    <div
      style={style}
      className={cn(
        s.root,
        className,
        reviews && s.reviews,
        withReplenishment && s.withReplenishment
      )}
      ref={rootRef}
      data-testid="client-suggest-field"
    >
      <Input
        ref={inputRef}
        onClick={() => {
          inputRef.current?.focus();
          setVisibility(true);
        }}
        onBlur={() => setVisibility(false)}
        onFocus={() => setVisibility(true)}
        name={`user-suggest-${getRandomNumber()}`}
        value={inputValue}
        onChange={(newInputValue) => {
          if (value) {
            return onChange(null);
          }
          setInputValue(newInputValue);
        }}
        leftContent={<SearchIcon color="primary" size="m" />}
        placeholder={placeholder ?? t('clients.search.placeholder')}
        hideBottomBorder={shouldShowSuggests}
        bordered={showBorder}
        autoComplete="off"
        disabled={disabled}
        variant="secondary"
        small={small}
        rightContent={
          isButtonClear && value ? (
            <Button
              variant="ghost"
              iconName="CloseIcon"
              size="s"
              onClick={() => {
                onChange(null);
              }}
              className={s.button}
            />
          ) : undefined
        }
        dataTestId="client-search"
      />
      {shouldShowSuggests && (
        <Dropdown
          items={items}
          className={withReplenishment ? s.dropdown : undefined}
          value={value}
          onChange={(uuid) => {
            onChange(uuid);
            setVisibility(false);
          }}
          renderItem={(item) =>
            withReplenishment ? (
              <div
                onMouseDown={(e) => e.preventDefault()}
                className={cn(s.suggest, s.suggest_deposit)}
                data-testid={item.original?.phone}
              >
                <div className={s.left}>
                  <AlienIcon color="link-info" className={s.icon} />
                  <span>{item.original?.phone}</span>
                </div>
                <div className={s.right}>
                  {item.original?.nickname && (
                    <span
                      className={cn(
                        !permissions?.has('payment_create') && s.login_big,
                        s.login
                      )}
                    >
                      {item.original.nickname}
                    </span>
                  )}
                  {permissions?.has('payment_create') && (
                    <Button
                      className={s.button}
                      onlyIcon
                      iconName="CashbackIcon"
                      variant="secondary"
                      onClick={(e) => {
                        e.preventDefault();
                        if (item?.original?.uuid && setIsDefaultClient)
                          setIsDefaultClient(item.original.uuid);
                        setDepositOpen && setDepositOpen(true);
                        setVisibility(false);
                        setInputValue('');

                        e.stopPropagation();
                      }}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className={s.suggest} data-testid={item.original?.phone}>
                <AlienIcon color="link-info" className={s.icon} />
                <span>{item.original?.phone}</span>
                {item.original?.nickname && (
                  <span className={s.login}>{item.original.nickname}</span>
                )}
              </div>
            )
          }
          dataTestId="client-suggests"
        />
      )}
    </div>
  );
};
